import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, PoundSterling, AlertTriangle, Trash2, Mail, FileSignature, Search, Check, ChevronsUpDown, Home } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

const BookingForm = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedCustomerId = searchParams.get('customer');
  const isEditMode = !!bookingId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [booking, setBooking] = useState({
    customer_id: preselectedCustomerId || '',
    collection_date: '',
    collection_time: '07:00',
    return_date: '',
    return_time: '19:00',
    special_instructions: '',
    total_cost: 0,
    amount_received: 0,
    booking_deposit_received: false,
    total_booking_fee_received: false,
    security_deposit_received: false,
    security_deposit_returned: false,
  });
  const [displayId, setDisplayId] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [API_URL]);

  const fetchBooking = useCallback(async () => {
    if (!bookingId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBooking({
          customer_id: data.customer_id || '',
          collection_date: data.collection_date || '',
          collection_time: data.collection_time || '07:00',
          return_date: data.return_date || '',
          return_time: data.return_time || '19:00',
          special_instructions: data.special_instructions || '',
          total_cost: data.total_cost || 0,
          amount_received: data.amount_received || 0,
          booking_deposit_received: data.booking_deposit_received || false,
          total_booking_fee_received: data.total_booking_fee_received || false,
          security_deposit_received: data.security_deposit_received || false,
          security_deposit_returned: data.security_deposit_returned || false,
        });
        setDisplayId(data.display_id || '');
      } else {
        toast.error('Booking not found');
        navigate('/admin/bookings');
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking');
    } finally {
      setLoading(false);
    }
  }, [API_URL, bookingId, navigate]);

  // Check for conflicts when dates change
  const checkConflicts = useCallback(async (collectionDate, returnDate) => {
    if (!collectionDate && !returnDate) {
      setConflictWarning(null);
      return;
    }

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/bookings/check-conflicts`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.has_conflict && data.conflicting_bookings.length > 0) {
              setConflictWarning(data.conflicting_bookings);
            } else {
              setConflictWarning(null);
            }
          } catch {
            setConflictWarning(null);
          }
        }
      };
      
      xhr.send(JSON.stringify({
        collection_date: collectionDate || null,
        return_date: returnDate || null,
        exclude_booking_id: bookingId || null
      }));
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
  }, [API_URL, bookingId]);

  useEffect(() => {
    fetchCustomers();
    if (isEditMode) {
      fetchBooking();
    }
  }, [isEditMode, fetchCustomers, fetchBooking]);

  // Check conflicts when dates change
  useEffect(() => {
    const timer = setTimeout(() => {
      checkConflicts(booking.collection_date, booking.return_date);
    }, 300);
    return () => clearTimeout(timer);
  }, [booking.collection_date, booking.return_date, checkConflicts]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    // When collection date is set, auto-set return date to match
    if (name === 'collection_date' && value) {
      // If return date is empty or before the new collection date, update it
      if (!booking.return_date || new Date(booking.return_date) < new Date(value)) {
        setBooking(prev => ({
          ...prev,
          collection_date: value,
          return_date: value
        }));
        return;
      }
    }
    
    // Validate return date is not before collection date
    if (name === 'return_date' && booking.collection_date && value) {
      if (new Date(value) < new Date(booking.collection_date)) {
        toast.error('Return date cannot be before collection date');
        return;
      }
    }
    
    setBooking(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    if (name === 'total_booking_fee_received' && checked) {
      // When Total Booking Fee Received is checked, set amount_received to total_cost
      setBooking(prev => ({ 
        ...prev, 
        [name]: checked,
        amount_received: prev.total_cost
      }));
    } else {
      setBooking(prev => ({ ...prev, [name]: checked }));
    }
  };

  // Auto-check/uncheck Total Booking Fee Received based on balance due
  useEffect(() => {
    const balanceDue = booking.total_cost - booking.amount_received;
    if (balanceDue <= 0 && booking.total_cost > 0 && !booking.total_booking_fee_received) {
      // Balance is zero or negative and there's a cost - auto-check
      setBooking(prev => ({ ...prev, total_booking_fee_received: true }));
    } else if (balanceDue > 0 && booking.total_booking_fee_received) {
      // Balance is positive - auto-uncheck
      setBooking(prev => ({ ...prev, total_booking_fee_received: false }));
    }
  }, [booking.total_cost, booking.amount_received]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!booking.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    if (!booking.collection_date || !booking.collection_time || !booking.return_date || !booking.return_time) {
      toast.error('Please fill in all date and time fields');
      return;
    }
    
    // Validate return date is not before collection date
    if (new Date(booking.return_date) < new Date(booking.collection_date)) {
      toast.error('Return date cannot be before collection date');
      return;
    }

    setSaving(true);
    
    const xhr = new XMLHttpRequest();
    const url = isEditMode 
      ? `${API_URL}/api/bookings/${bookingId}`
      : `${API_URL}/api/bookings`;
    
    xhr.open(isEditMode ? 'PUT' : 'POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        setSaving(false);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(isEditMode ? 'Booking updated' : 'Booking created');
          navigate('/admin/bookings');
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            toast.error(data.detail || 'Failed to save booking');
          } catch {
            toast.error('Failed to save booking');
          }
        }
      }
    };
    
    xhr.onerror = function() {
      setSaving(false);
      toast.error('Failed to save booking');
    };
    
    xhr.send(JSON.stringify(booking));
  };

  const handleDelete = async () => {
    setDeleting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Booking deleted');
        navigate('/admin/bookings');
      } else {
        toast.error('Failed to delete booking');
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const balanceDue = booking.total_cost - booking.amount_received;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c9a55a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="booking-form-page">
      {/* Header */}
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/bookings')}
                className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
                title="Back to Bookings"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#f5f3ed]">
                {isEditMode ? 'Edit Booking' : 'New Booking'}
              </h1>
              {displayId && (
                <p className="text-[#c9a55a] text-sm">{displayId}</p>
              )}
            </div>
          </div>
          {isEditMode && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => navigate(`/admin/bookings/${bookingId}/handover`)}
                variant="outline"
                className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
                data-testid="handover-btn"
              >
                <FileSignature className="w-4 h-4 mr-2" />
                Handover Form
              </Button>
              <Button
                onClick={() => navigate(`/admin/bookings/${bookingId}/send-email`)}
                variant="outline"
                className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
                data-testid="send-message-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send a Message
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="bg-red-500 hover:bg-red-600"
                data-testid="delete-booking-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          {/* Customer Selection */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Customer</h2>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-[#f5f3ed]/70">Select Customer *</Label>
                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={customerSearchOpen}
                      className="w-full justify-between bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#1a1f1a]/70 hover:text-[#f5f3ed]"
                      data-testid="customer-select"
                    >
                      {booking.customer_id ? (
                        (() => {
                          const selected = customers.find(c => c.customer_id === booking.customer_id);
                          if (selected) {
                            return `${selected.display_id ? `${selected.display_id} - ` : ''}${selected.first_name} ${selected.surname}`;
                          }
                          return "Select a customer...";
                        })()
                      ) : (
                        "Select a customer..."
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0 bg-[#1a1f1a] border-[#2d5f3f]" align="start">
                    <Command className="bg-[#1a1f1a]">
                      <CommandInput 
                        placeholder="Search customers by name, email..." 
                        className="text-[#f5f3ed]"
                      />
                      <CommandList>
                        <CommandEmpty className="text-[#f5f3ed]/70 py-4 text-center">
                          No customer found.
                        </CommandEmpty>
                        <CommandGroup>
                          {customers.map((customer) => (
                            <CommandItem
                              key={customer.customer_id}
                              value={`${customer.display_id || ''} ${customer.first_name} ${customer.surname} ${customer.email || ''} ${customer.telephone || ''}`}
                              onSelect={() => {
                                setBooking(prev => ({ ...prev, customer_id: customer.customer_id }));
                                setCustomerSearchOpen(false);
                              }}
                              className="text-[#f5f3ed] hover:bg-[#2d5f3f]/50 cursor-pointer"
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${booking.customer_id === customer.customer_id ? "opacity-100 text-[#c9a55a]" : "opacity-0"}`}
                              />
                              <div className="flex flex-col">
                                <span>
                                  {customer.display_id ? `${customer.display_id} - ` : ''}{customer.first_name} {customer.surname}
                                </span>
                                {customer.email && (
                                  <span className="text-xs text-[#f5f3ed]/50">{customer.email}</span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              {booking.customer_id && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/admin/customers/${booking.customer_id}`)}
                  className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10 mt-6"
                  data-testid="view-customer-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  View Customer
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/customers/new')}
                className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30 mt-6"
              >
                <User className="w-4 h-4 mr-2" />
                New Customer
              </Button>
            </div>
          </div>

          {/* Dates and Times */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Collection & Return</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">Collection Date *</Label>
                <Input
                  type="date"
                  name="collection_date"
                  value={booking.collection_date}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[10deg]"
                  required
                  data-testid="collection-date"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Collection Time *</Label>
                <Input
                  type="time"
                  name="collection_time"
                  value={booking.collection_time}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[10deg]"
                  required
                  data-testid="collection-time"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Return Date *</Label>
                <Input
                  type="date"
                  name="return_date"
                  value={booking.return_date}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[10deg]"
                  required
                  data-testid="return-date"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Return Time *</Label>
                <Input
                  type="time"
                  name="return_time"
                  value={booking.return_time}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[10deg]"
                  required
                  data-testid="return-time"
                />
              </div>
            </div>

            {/* Conflict Warning - Displayed directly below dates */}
            {conflictWarning && conflictWarning.length > 0 && (
              <div className="mt-4 bg-amber-500/20 border border-amber-500/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-200 font-medium">Booking Conflict Warning</p>
                    <p className="text-amber-200/80 text-sm mt-1">
                      The following booking(s) already exist for these dates:
                    </p>
                    <ul className="mt-2 space-y-1">
                      {conflictWarning.map((conflict, idx) => (
                        <li key={idx} className="text-amber-200 text-sm">
                          • <strong>{conflict.customer_name}</strong> ({conflict.collection_date} to {conflict.return_date})
                        </li>
                      ))}
                    </ul>
                    <p className="text-amber-200/60 text-xs mt-2">
                      You can still create this booking. It will be shown in red on the calendar.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Special Instructions */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Special Instructions / Notes</h2>
            
            <Textarea
              name="special_instructions"
              value={booking.special_instructions}
              onChange={handleInputChange}
              className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
              rows={4}
              placeholder="Any special requirements or notes for this booking..."
              data-testid="special-instructions"
            />
          </div>

          {/* Payment Information */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Payment Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label className="text-[#f5f3ed]/70">Total Cost (£)</Label>
                <Input
                  type="number"
                  name="total_cost"
                  value={booking.total_cost}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  step="0.01"
                  min="0"
                  data-testid="total-cost"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Amount Received (£)</Label>
                <Input
                  type="number"
                  name="amount_received"
                  value={booking.amount_received}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  step="0.01"
                  min="0"
                  data-testid="amount-received"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Balance Due</Label>
                <div className={`mt-2 p-3 rounded-lg ${balanceDue > 0 ? 'bg-[#c9a55a]/20' : 'bg-green-500/20'}`}>
                  <div className="flex items-center gap-2">
                    <PoundSterling className={`w-5 h-5 ${balanceDue > 0 ? 'text-[#c9a55a]' : 'text-green-400'}`} />
                    <span className={`text-lg font-semibold ${balanceDue > 0 ? 'text-[#c9a55a]' : 'text-green-400'}`}>
                      £{balanceDue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Checkboxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-3 rounded-lg bg-[#1a1f1a]/50">
                <Checkbox
                  id="booking_deposit_received"
                  checked={booking.booking_deposit_received}
                  onCheckedChange={(checked) => handleCheckboxChange('booking_deposit_received', checked)}
                  className="border-[#c9a55a] data-[state=checked]:bg-[#c9a55a] data-[state=checked]:border-[#c9a55a]"
                  data-testid="booking-deposit-checkbox"
                />
                <Label htmlFor="booking_deposit_received" className="text-[#f5f3ed] cursor-pointer">
                  Booking Deposit Received
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 rounded-lg bg-[#1a1f1a]/50">
                <Checkbox
                  id="total_booking_fee_received"
                  checked={booking.total_booking_fee_received}
                  onCheckedChange={(checked) => handleCheckboxChange('total_booking_fee_received', checked)}
                  className="border-[#c9a55a] data-[state=checked]:bg-[#c9a55a] data-[state=checked]:border-[#c9a55a]"
                  data-testid="total-fee-checkbox"
                />
                <Label htmlFor="total_booking_fee_received" className="text-[#f5f3ed] cursor-pointer">
                  Total Booking Fee Received
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 rounded-lg bg-[#1a1f1a]/50">
                <Checkbox
                  id="security_deposit_received"
                  checked={booking.security_deposit_received}
                  onCheckedChange={(checked) => handleCheckboxChange('security_deposit_received', checked)}
                  className="border-[#c9a55a] data-[state=checked]:bg-[#c9a55a] data-[state=checked]:border-[#c9a55a]"
                  data-testid="security-deposit-checkbox"
                />
                <Label htmlFor="security_deposit_received" className="text-[#f5f3ed] cursor-pointer">
                  Security Deposit Received
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 rounded-lg bg-[#1a1f1a]/50">
                <Checkbox
                  id="security_deposit_returned"
                  checked={booking.security_deposit_returned}
                  onCheckedChange={(checked) => handleCheckboxChange('security_deposit_returned', checked)}
                  className="border-[#c9a55a] data-[state=checked]:bg-[#c9a55a] data-[state=checked]:border-[#c9a55a]"
                  data-testid="security-returned-checkbox"
                />
                <Label htmlFor="security_deposit_returned" className="text-[#f5f3ed] cursor-pointer">
                  Security Deposit Returned
                </Label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/bookings')}
              className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
              data-testid="save-booking-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a1f1a]" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {isEditMode ? 'Save Changes' : 'Create Booking'}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Booking?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will permanently delete this booking. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingForm;
